import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, BellRing, Calendar, XCircle, Info, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'reminder',     icon: BellRing, ar: 'تذكيرات المواعيد',        en: 'Appointment reminders' },
  { key: 'booking',      icon: Calendar, ar: 'إشعارات الحجز',            en: 'Booking notifications' },
  { key: 'cancellation', icon: XCircle,  ar: 'إشعارات الإلغاء/التغيير',  en: 'Cancellation / reschedule alerts' },
  { key: 'general',      icon: Info,     ar: 'الإشعارات العامة',         en: 'General notifications' },
  // consultation is intentionally not user-toggleable (safety-critical)
];

export const NotificationPreferences = () => {
  const { t } = useLanguage();
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    axios.get(`${API}/notifications/preferences`, { withCredentials: true })
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ reminder: true, booking: true, cancellation: true, general: true, consultation: true }));
  }, []);

  if (!prefs) {
    return (
      <div className="text-center text-slate-500 py-6 text-sm" data-testid="prefs-loading">
        {t('جاري التحميل...', 'Loading...')}
      </div>
    );
  }

  return (
    <Card className="p-5 space-y-4" data-testid="notification-preferences">
      <div className="flex items-center gap-2">
        <Bell size={18} className="text-[#1565c0]" />
        <h3 className="font-bold text-slate-800">
          {t('تفضيلات الإشعارات', 'Notification preferences')}
        </h3>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        {t(
          'جميع الإشعارات مفعّلة دائماً لضمان وصول تنبيهات المواعيد والمكالمات والدفع في الوقت المناسب.',
          'All notifications are always on so appointment, call and payment alerts always reach you on time.',
        )}
      </p>

      <div className="space-y-2">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100"
              data-testid={`pref-row-${c.key}`}
            >
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-sm">
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{t(c.ar, c.en)}</p>
                <p className="text-[11px] text-emerald-700 mt-0.5">
                  {t('مفعّلة دائماً', 'Always on')}
                </p>
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 flex-shrink-0"
                data-testid={`pref-switch-${c.key}`}
              >
                <Check size={12} strokeWidth={3} />
                {t('مفعّل', 'ON')}
              </span>
            </div>
          );
        })}

        {/* Read-only critical row */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="pref-row-consultation">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-sm">
            <BellRing size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">
              {t('تنبيهات الاستشارة الحية', 'Live consultation alerts')}
            </p>
            <p className="text-[11px] text-emerald-700 mt-0.5">
              {t('مفعّلة دائماً — لا يمكن تعطيلها', 'Always on — cannot be disabled')}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 flex-shrink-0">
            <Check size={12} strokeWidth={3} />
            {t('مفعّل', 'ON')}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default NotificationPreferences;
